/**
 * 🎯 Admin Filter Management Service
 * Complete CRUD operations for filter options management
 */

import apiClient, { API_BASE } from './apiClient';
import {
  FilterOption,
  FilterCategory,
  CreateFilterOptionDTO,
  UpdateFilterOptionDTO,
  ListFilterOptionsParams,
  FilterAnalyticsParams,
  FilterOptionResponse,
  FilterOptionsListResponse,
  FilterCategoriesResponse,
  FilterAnalyticsResponse,
  ApiResponse,
} from '@/types/filters';

// ============================================
// Service Methods
// ============================================

export const filterManagementService = {
  /**
   * 📋 List all filter options with pagination and filtering
   * @param params - Query parameters for filtering and pagination
   * @returns Paginated list of filter options
   */
  async listFilterOptions(
    params?: ListFilterOptionsParams
  ): Promise<FilterOptionsListResponse> {
    try {
      const response = await apiClient.get<FilterOptionsListResponse>(
        `${API_BASE}/admin/filters/options`,
        { params }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to list filter options:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load filter options',
      } as any;
    }
  },

  /**
   * 🔍 Get a specific filter option by ID
   * @param id - Filter option ID
   * @returns Single filter option details
   */
  async getFilterOption(id: string): Promise<FilterOptionResponse> {
    try {
      const response = await apiClient.get<FilterOptionResponse>(
        `${API_BASE}/admin/filters/options/${id}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to get filter option:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load filter option',
      } as any;
    }
  },

  /**
   * ➕ Create a new filter option
   * @param data - Filter option data
   * @returns The created filter option
   */
  async createFilterOption(
    data: CreateFilterOptionDTO
  ): Promise<FilterOptionResponse> {
    try {
      const response = await apiClient.post<FilterOptionResponse>(
        `${API_BASE}/admin/filters/options/create`,
        data
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to create filter option:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to create filter option',
        details: error.response?.data?.details,
      } as any;
    }
  },

  /**
   * ✏️ Update an existing filter option
   * @param id - Filter option ID
   * @param data - Fields to update (partial)
   * @returns The updated filter option
   */
  async updateFilterOption(
    id: string,
    data: UpdateFilterOptionDTO
  ): Promise<FilterOptionResponse> {
    try {
      const response = await apiClient.put<FilterOptionResponse>(
        `${API_BASE}/admin/filters/options/${id}/update`,
        data
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to update filter option:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update filter option',
        details: error.response?.data?.details,
      } as any;
    }
  },

  /**
   * 🗑️ Delete a filter option (soft delete)
   * @param id - Filter option ID
   * @returns Deletion confirmation
   */
  async deleteFilterOption(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        `${API_BASE}/admin/filters/options/${id}/delete`
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to delete filter option:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete filter option',
      };
    }
  },

  /**
   * 📊 Get analytics data for filter usage
   * @param params - Query parameters for date range and filtering
   * @returns Analytics data including usage stats and trends
   */
  async getFilterAnalytics(
    params?: FilterAnalyticsParams
  ): Promise<FilterAnalyticsResponse> {
    try {
      const response = await apiClient.get<FilterAnalyticsResponse>(
        `${API_BASE}/admin/filters/analytics`,
        { params }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to get filter analytics:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load analytics',
      } as any;
    }
  },

  /**
   * 📁 List all filter categories
   * @returns List of all filter categories
   */
  async listFilterCategories(): Promise<FilterCategoriesResponse> {
    try {
      const response = await apiClient.get<FilterCategoriesResponse>(
        `${API_BASE}/admin/filters/categories`
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to list filter categories:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load categories',
      } as any;
    }
  },

  /**
   * 🔄 Bulk update display order for multiple filters
   * @param updates - Array of {id, display_order} objects
   * @returns Success confirmation
   */
  async bulkUpdateDisplayOrder(
    updates: Array<{ id: string; display_order: number }>
  ): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post<ApiResponse<void>>(
        `${API_BASE}/admin/filters/options/bulk-reorder`,
        { updates }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk update display order:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update display order',
      };
    }
  },

  /**
   * 📤 Bulk publish multiple filter options
   * @param ids - Array of filter option IDs
   * @returns Success confirmation
   */
  async bulkPublish(ids: string[]): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post<ApiResponse<void>>(
        `${API_BASE}/admin/filters/options/bulk-publish`,
        { ids }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk publish filters:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to publish filters',
      };
    }
  },

  /**
   * 📥 Bulk unpublish multiple filter options
   * @param ids - Array of filter option IDs
   * @returns Success confirmation
   */
  async bulkUnpublish(ids: string[]): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post<ApiResponse<void>>(
        `${API_BASE}/admin/filters/options/bulk-unpublish`,
        { ids }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk unpublish filters:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to unpublish filters',
      };
    }
  },

  /**
   * 🗑️ Bulk delete multiple filter options
   * @param ids - Array of filter option IDs
   * @returns Success confirmation
   */
  async bulkDelete(ids: string[]): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post<ApiResponse<void>>(
        `${API_BASE}/admin/filters/options/bulk-delete`,
        { ids }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk delete filters:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete filters',
      };
    }
  },

  /**
   * 🔄 Restore a soft-deleted filter option
   * @param id - Filter option ID
   * @returns The restored filter option
   */
  async restoreFilterOption(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        `${API_BASE}/admin/filters/options/${id}/restore`
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to restore filter option:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to restore filter option',
      };
    }
  },

  /**
   * 📜 Get audit log for filter changes
   * @param params - Query parameters for filtering audit log
   * @returns Audit log entries with pagination
   */
  async getAuditLog(params?: {
    entityId?: string;
    action?: string;
    changedBy?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.get<ApiResponse<any>>(
        `${API_BASE}/admin/filters/audit-log`,
        { params }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to get audit log:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load audit log',
      };
    }
  },
};

export default filterManagementService;
